import React, { useState } from 'react';
import WideContent from 'core/layout/content/wide-content';
import Translate from 'ui/atoms/translate';
import Hint from 'ui/atoms/hint';
import LoadingRing from 'ui/atoms/loading-ring';
import WithAuthenticatedPage from 'core/auth/components/with-authenticated-page';
import { AppType } from 'core/auth/types';
import Section from 'ui/atoms/section';
import ActionButtons from 'ui/molecules/action-buttons';
import { makeBigNumber } from 'ui/helper/big-number';
import ProductsOverview from './products-overview';
import { useIssuerOverview } from 'apps/issuer/hooks';
import Link from 'core/routing/link';
import * as Styled from './styled';
import Header from 'src/ui/atoms/header';
import useIsMedia, { DEVICES } from 'src/ui/hooks/use-is-media';
import IssuerInvestmentsTable from '../../shared/investments-table';
import { hasDistributionViewDataPermission } from '../../helpers/distributionPlatformPermissions';
import { hasViewPermission } from '../../helpers/tokenPermissions';
import { useCurrentUserSelector } from 'src/core/auth/hooks';
import PageWrapper from 'src/core/layout/content/page-wrapper';

const IssuerOverviewPage = () => {
  const [filterPopupHeight, setFilterPopupHeight] = useState(0);

  const isPhone = useIsMedia(DEVICES.phone);

  const { currentUser } = useCurrentUserSelector();

  const { requiredActions, loading, productsOverview, investorsIncompleteList } = useIssuerOverview();

  if (loading || !productsOverview || !investorsIncompleteList) {
    return <LoadingRing />;
  }

  const hasPermission =
    hasDistributionViewDataPermission(currentUser?.associatedDistributionPlatforms || []) ||
    hasViewPermission(currentUser);

  return (
    <PageWrapper paddingBottom={filterPopupHeight}>
      <WideContent title={<Translate name="dashboard.issuerOverview" />} titleSpacing="larger">
        <ProductsOverview data={productsOverview} />
        <Section>
          <>
            {((requiredActions && requiredActions?.length > 0) || investorsIncompleteList.length > 0) && (
              <Header size="small" spacing="medium">
                <Translate name="dashboardIssuerOverview.openTasks" />
              </Header>
            )}
            {requiredActions &&
              requiredActions.map(({ type, number, link }) => (
                <Hint variant="info" key={type}>
                  <div>
                    {!isPhone && (
                      <Translate
                        name={`dashboardIssuerOverview.requiredActions.${type}`}
                        grammaticalNumber={makeBigNumber(number)}
                        args={[
                          number,
                          (text, key) => (
                            <Link to={link} key={key} inline>
                              <Styled.RedirectButton variant="secondary" size="xsmall">
                                {text}
                              </Styled.RedirectButton>
                            </Link>
                          ),
                        ]}
                      />
                    )}
                    {isPhone && (
                      <>
                        <Translate
                          name={`dashboardIssuerOverview.requiredActionsMobile.${type}`}
                          grammaticalNumber={makeBigNumber(number)}
                          args={[number]}
                        />
                        <div>
                          <Link to={link} key={type}>
                            <Styled.RedirectButton variant="secondary" size="xsmall">
                              <Translate name={`dashboardIssuerOverview.requiredActionsButton.${type}`} />
                            </Styled.RedirectButton>
                          </Link>
                        </div>
                      </>
                    )}
                  </div>
                </Hint>
              ))}
            {investorsIncompleteList &&
              investorsIncompleteList.map((investor) => (
                <Hint variant="info" key={investor.id}>
                  <div>
                    {!isPhone && (
                      <Translate
                        name="dashboardIssuerOverview.requiredActions.incompleteInvestor"
                        args={[
                          investor.investorName,
                          investor.email,
                          (text, key) => (
                            <Link to={`investor/${investor.id}`} key={key} inline underline>
                              {text}
                            </Link>
                          ),
                        ]}
                      />
                    )}
                    {isPhone && (
                      <>
                        <Translate
                          name="dashboardIssuerOverview.requiredActionsMobile.incompleteInvestor"
                          args={[investor.investorName, investor.email]}
                        />
                        <div>
                          <Link to={`issuerInvestor/${investor.id}`} underline>
                            <Translate name={`dashboardIssuerOverview.requiredActionsButton.incompleteInvestor`} />
                          </Link>
                        </div>
                      </>
                    )}
                  </div>
                </Hint>
              ))}
          </>
        </Section>
        <>
          <Header size="small" spacing="medium">
            <Translate name="dashboardIssuerOverview.lastInvestments" />
          </Header>
          <IssuerInvestmentsTable
            defaultLimit={3}
            hasPermission={hasPermission}
            availableLimits={[3, 10, 50, 100]}
            hidePaginationNavigation={true}
            setFilterPopupHeight={setFilterPopupHeight}
          />
          <ActionButtons>
            <Link to="/dashboard/investments">
              <Translate name="dashboardIssuerOverview.viewAllInvestments" />
            </Link>
          </ActionButtons>
        </>
      </WideContent>
    </PageWrapper>
  );
};

export default WithAuthenticatedPage(IssuerOverviewPage, AppType.ISSUER);
